// Shared bits for the Emergency Numbers tabs.
export const STATUS = {
  blocked:    { label: "Blocked",          tone: "bg-rose-100 text-rose-800",
                hint: "Something is unproven — a human has to look before anything is touched." },
  needs_work: { label: "Needs work",       tone: "bg-amber-100 text-amber-800",
                hint: "A safe plan exists and is waiting for a manager's approval." },
  address_missing: { label: "Address missing", tone: "bg-orange-100 text-orange-800",
                hint: "DLR domain where real users have no emergency address of their own and no domain default to fall back on — or point at an address the domain does not hold, or one different from what their Endpoint carries." },
  review:     { label: "Manager decision", tone: "bg-sky-100 text-sky-800",
                hint: "SIP trunk AND regular users — decide Static or DLR before anything is planned." },
  ui_only:    { label: "UI config only",   tone: "bg-slate-200 text-slate-700",
                hint: "Only the Portal's 911 tabs are set wrong; the registrations are fine." },
  error:      { label: "Error",            tone: "bg-rose-50 text-rose-700",
                hint: "The platform did not answer for this domain." },
  hybrid:     { label: "Hybrid",           tone: "bg-slate-100 text-slate-600",
                hint: "On the exception list — runs Legacy and DLR together on purpose." },
  correct:    { label: "Correct",          tone: "bg-emerald-100 text-emerald-800",
                hint: "Set up exactly as its mode requires." },
};
export const STATUS_ORDER = ["blocked", "needs_work", "address_missing", "review", "ui_only", "error", "hybrid", "correct"];

// How a subscriber's emergency address / number resolves on a DLR domain (from the assessor).
export const ADDRESS_STATE = {
  own:      { label: "own address",    tone: "bg-emerald-50 text-emerald-700" },
  inherits: { label: "domain default", tone: "bg-slate-100 text-slate-600" },
  none:     { label: "no address",     tone: "bg-rose-100 text-rose-800" },
  unknown:  { label: "unknown id",     tone: "bg-amber-100 text-amber-800" },
  ok:       { label: "set",            tone: "bg-emerald-50 text-emerald-700" },
};
export const NUMBER_STATE = {
  own:      { label: "endpoint",       tone: "bg-emerald-50 text-emerald-700" },
  inherits: { label: "domain default", tone: "bg-slate-100 text-slate-600" },
  none:     { label: "no number",      tone: "bg-rose-100 text-rose-800" },
  unknown:  { label: "not an endpoint", tone: "bg-rose-100 text-rose-800" },
  ok:       { label: "endpoint",       tone: "bg-emerald-50 text-emerald-700" },
};
export const BAD_STATES = ["none", "unknown"];

export const MODE = {
  LEGACY: { label: "Static",  long: "Static (Emergency Numbers · Legacy)", tone: "bg-indigo-100 text-indigo-800" },
  DLR:    { label: "DLR",     long: "DLR (Emergency Endpoints + Addresses)", tone: "bg-teal-100 text-teal-800" },
  REVIEW: { label: "Undecided", long: "Undecided — manager decision", tone: "bg-sky-100 text-sky-800" },
};

export const NUMBER_STATUS = {
  not_registered: { label: "Not registered", tone: "bg-rose-100 text-rose-800",
                    hint: "The Portal knows this number but Bandwidth holds nothing for it — a 911 call carries no address." },
  wrong:          { label: "Wrong side",     tone: "bg-amber-100 text-amber-800",
                    hint: "Registered at Bandwidth, but on the opposite side of the domain's mode." },
  unused:         { label: "Unused endpoint", tone: "bg-sky-100 text-sky-800",
                    hint: "An Emergency Endpoint on the Portal that no subscriber uses and Bandwidth never learned — no 911 gap, just clutter. Open the domain to delete it." },
  bandwidth_only: { label: "Bandwidth only", tone: "bg-slate-200 text-slate-700",
                    hint: "Bandwidth holds it, no Portal domain uses it." },
  registered:     { label: "Registered",     tone: "bg-emerald-100 text-emerald-800",
                    hint: "Portal and Bandwidth agree." },
};
export const NUMBER_STATUS_ORDER = ["not_registered", "wrong", "unused", "bandwidth_only", "registered"];

export const SIDE = {
  legacy: "Legacy", dlr: "Endpoint", user_only: "User only", none: "—",
  static: "Static", user: "User",
};

export const PLATFORM_LABEL = { core: "Core", sas: "SAS" };

export const clock = (iso) => (iso ? iso.replace("T", " ").replace("Z", "").slice(0, 16) : "—");

export const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

export const fmtTn = (tn) => {
  const d = String(tn || "").replace(/\D/g, "");
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : tn || "—";
};

export function Pill({ children, tone = "bg-slate-100 text-slate-700", title, testid }) {
  return (
    <span title={title} data-testid={testid}
          className={`inline-flex items-center whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}>
      {children}
    </span>
  );
}

export function StatusPill({ status, testid }) {
  const s = STATUS[status] || { label: status || "—", tone: "bg-slate-100 text-slate-600" };
  return <Pill tone={s.tone} title={s.hint} testid={testid}>{s.label}</Pill>;
}

export function ModePill({ mode, testid }) {
  const m = MODE[mode] || { label: mode || "—", tone: "bg-slate-100 text-slate-600" };
  return <Pill tone={m.tone} title={m.long} testid={testid}>{m.label}</Pill>;
}

export function Stat({ label, value, hint, tone, testid, onClick, active }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testid}
         className={`rounded-lg border bg-white px-4 py-3 text-left transition-colors ${
           onClick ? "hover:border-slate-300 hover:bg-slate-50" : ""} ${
           active ? "border-primary ring-1 ring-primary/30" : "border-slate-200"}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-xl font-semibold ${tone || "text-slate-800"}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
    </Tag>
  );
}

export function Empty({ children, testid, colSpan }) {
  const body = <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid={testid}>{children}</div>;
  return colSpan ? <tr><td colSpan={colSpan}>{body}</td></tr> : body;
}

export function Pager({ page, pages, onPage, testid = "e911" }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 text-sm">
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}
              className="rounded border border-slate-200 px-2 py-1 text-xs disabled:opacity-40">Previous</button>
      <span className="text-xs text-slate-500">Page {page} of {pages}</span>
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}
              className="rounded border border-slate-200 px-2 py-1 text-xs disabled:opacity-40">Next</button>
    </div>
  );
}
